import { logEvent } from "@/lib/monitoring";
import { toast } from "sonner";

const DEV_SUPPRESS_PATTERNS = [
  'useAuth must be used within AuthProvider',
];

// Transient/benign noise that should never be logged as a critical runtime error.
// These are background fetch failures (cold-start 503s, network blips) that the
// caller already retries — they don't break the UI.
const ALWAYS_SUPPRESS_PATTERNS = [
  'SUPABASE_EDGE_RUNTIME_ERROR',
  'Service is temporarily unavailable',
  'Edge function returned 503',
  'ai-proactive-engage',
  'Failed to fetch',
  'NetworkError when attempting to fetch',
  'Load failed',
];

function shouldSuppress(message: string | undefined | null): boolean {
  const m = message || '';
  if (ALWAYS_SUPPRESS_PATTERNS.some((p) => m.includes(p))) return true;
  if (import.meta.env.DEV && DEV_SUPPRESS_PATTERNS.some((p) => m.includes(p))) return true;
  return false;
}

let installed = false;
let firstCaptureNotified = false;

interface AppFrame { fn: string; file: string; line: number | null; col: number | null }

/**
 * Parse a JS error stack and return the first frame that points at the
 * application's own source (anything under `/src/`), skipping vendor/React
 * internals. Returns null if nothing matches.
 */
function parseFirstAppFrame(stack: string | undefined | null): AppFrame | null {
  if (!stack || typeof stack !== "string") return null;
  const lines = stack.split("\n").map((l) => l.trim()).filter(Boolean);
  const VENDOR = /(node_modules\/\.vite|chunk-[A-Z0-9]+\.js|react-dom|react-router|framer-motion|@supabase)/i;
  for (const line of lines) {
    if (!line.includes("/src/")) continue;
    if (VENDOR.test(line)) continue;
    // Match: "at fnName (https://host/src/path/file.tsx:LINE:COL)"
    // or:    "at https://host/src/path/file.tsx:LINE:COL"
    let m = line.match(/at\s+([^\s(]+)\s+\(.*?(\/src\/[^):]+):(\d+):(\d+)\)/);
    if (m) return { fn: m[1], file: m[2].replace(/^\//, ""), line: Number(m[3]), col: Number(m[4]) };
    m = line.match(/at\s+.*?(\/src\/[^):]+):(\d+):(\d+)/);
    if (m) return { fn: "<anonymous>", file: m[1].replace(/^\//, ""), line: Number(m[2]), col: Number(m[3]) };
  }
  return null;
}

function captureContext() {
  try {
    return {
      route: typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
    };
  } catch {
    return { route: null, user_agent: null };
  }
}

function notifyFirstCapture() {
  if (firstCaptureNotified) return;
  firstCaptureNotified = true;
  try {
    toast.error("A runtime error was captured", {
      description: "Logged to admin monitoring.",
      action: {
        label: "Open monitoring",
        onClick: () => {
          window.location.href = "/admin?tab=health&subtab=incidents";
        },
      },
      duration: 6000,
    });
  } catch { /* ignore toast errors */ }
}

/**
 * Install global error listeners that report uncaught exceptions and
 * unhandled promise rejections into app_logs as critical events.
 * Safe to call multiple times.
 */
export function installClientErrorCapture() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    try {
      const err = event.error;
      const msg = event.message || String(err) || "Uncaught error";
      if (shouldSuppress(msg) || shouldSuppress(err?.message)) return;
      const ctx = captureContext();
      const first_app_frame = parseFirstAppFrame(err?.stack);
      logEvent(
        "ERROR",
        msg,
        {
          stack: err?.stack,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          source: "window.onerror",
          route: ctx.route,
          user_agent: ctx.user_agent,
          first_app_frame,
        },
        "critical",
      );
      notifyFirstCapture();
    } catch { /* never throw */ }
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    try {
      const reason: any = event.reason;
      const msg = reason?.message || String(reason) || "Unhandled rejection";
      if (shouldSuppress(msg)) return;
      const ctx = captureContext();
      const first_app_frame = parseFirstAppFrame(reason?.stack);
      logEvent(
        "ERROR",
        msg,
        {
          stack: reason?.stack,
          source: "unhandledrejection",
          route: ctx.route,
          user_agent: ctx.user_agent,
          first_app_frame,
        },
        "critical",
      );
      notifyFirstCapture();
    } catch { /* never throw */ }
  });
}
